import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BarList, DistributionBar, MeterBar, MiniStat, ScoreRing, TrendChart, scoreTone } from '../../../components/design-system/analytics';
import { KpiCard, SectionCard, StatusBadge, formatDate, formatNumber } from '../../../components/design-system/admin';
import { MarketCount, MarketData } from '../../../common/hooks/use-market-api.hook';
import {
    authorityLevel,
    authorityScore,
    followSplit,
    historyChange,
    historyTrend,
    linkDiversity,
    spamLevel,
} from '../../../modules/vendor/market/market-analysis';
import { backlinkInsights } from '../../../modules/vendor/market/market-insights';
import { monthLabel, seriesOf } from '../../../modules/vendor/market/market-presentation';
import { Nothing, Reading, Term } from './market-shared';

/**
 * Who points at the vendor's site, and what that is worth.
 *
 * Authority first, read in words, then how the number of linking sites is
 * moving, then what the links are made of — followed or not, which kinds of
 * site, which countries — and what to do about broken and spammy ones.
 */
export const BacklinksView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t, i18n } = useTranslation();
    const b = data.backlinks;
    const specs = useMemo(() => backlinkInsights(data), [data]);
    const history = data.history?.backlinks;

    if (!b) return <Nothing title={t('BUSINESS.BACKLINKS.NONE')} description={t('MARKET.EMPTY_BODY')} />;

    const authority = authorityScore(b.rank);
    const level = authorityLevel(authority);
    const since = (key: string) => {
        const ch = historyChange(history, key);
        return ch ? { delta: { current: ch.current, previous: ch.previous }, since: t('BUSINESS.COMMON.SINCE', { month: monthLabel(ch.since, i18n.language) }) } : null;
    };
    const hint = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');
    const domains = since('referringDomains');
    const links = since('backlinks');
    const broken = since('brokenBacklinks');
    const series = seriesOf(history, 'referringDomains').map((p) => ({ label: monthLabel(p.month, i18n.language), value: p.value }));

    const follow = followSplit(b);
    const spam = spamLevel(b.spamScore);
    const diversity = linkDiversity(b);

    const name = (group: 'TYPE' | 'PLATFORM' | 'ATTRIBUTE', key: string) =>
        key === 'unknown' ? t('BUSINESS.COMMON.UNKNOWN') : t(`BUSINESS.BACKLINKS.${group}.${key.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`, { defaultValue: key });
    const country = (code: string) => {
        if (code === 'unknown') return t('BUSINESS.COMMON.UNKNOWN');
        if (code === 'WW') return t('BUSINESS.BACKLINKS.WORLDWIDE');
        try {
            return new (Intl as any).DisplayNames([i18n.language], { type: 'region' }).of(code.toUpperCase()) ?? code;
        } catch {
            return code;
        }
    };
    const list = (rows: MarketCount[] | undefined, label: (k: string) => string) =>
        (rows ?? []).filter((r) => r.count > 0).map((r) => ({ key: r.key, label: label(r.key), value: r.count }));

    const composition = [
        { key: 'types', title: t('BUSINESS.BACKLINKS.TYPES'), hint: t('BUSINESS.BACKLINKS.TYPES_HINT'), items: list(b.linkTypes, (k) => name('TYPE', k)), bar: true },
        { key: 'platforms', title: t('BUSINESS.BACKLINKS.PLATFORMS'), hint: t('BUSINESS.BACKLINKS.PLATFORMS_HINT'), items: list(b.platforms, (k) => name('PLATFORM', k)) },
        { key: 'countries', title: t('BUSINESS.BACKLINKS.COUNTRIES'), hint: t('BUSINESS.BACKLINKS.COUNTRIES_HINT'), items: list(b.countries, country) },
        { key: 'tlds', title: t('BUSINESS.BACKLINKS.TLDS'), hint: t('BUSINESS.BACKLINKS.TLDS_HINT'), items: list(b.tlds, (k) => (k === 'unknown' ? t('BUSINESS.COMMON.UNKNOWN') : `.${k}`)) },
    ].filter((c) => c.items.length > 0);

    return (
        <div className="flex flex-col gap-6">
            <Reading specs={specs} />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <SectionCard title={<Term k="AUTHORITY" />} testId="business-authority">
                    <ScoreRing
                        value={authority}
                        size={88}
                        tone={authority == null ? 'neutral' : scoreTone(authority, { good: 40, bad: 20 })}
                        label={level ? t(`BUSINESS.BACKLINKS.LEVEL.${level.toUpperCase()}`) : undefined}
                        sublabel={t('BUSINESS.BACKLINKS.OUT_OF')}
                    />
                </SectionCard>
                <KpiCard
                    label={t('BUSINESS.TERMS.REFERRING_DOMAINS.LABEL')}
                    value={formatNumber(b.referringDomains)}
                    delta={domains?.delta}
                    hint={hint([domains?.since, t('MARKET.BACKLINKS.SITES_HINT')])}
                    trend={historyTrend(history, 'referringDomains')}
                />
                <KpiCard
                    label={t('MARKET.BACKLINKS.LINKS')}
                    value={formatNumber(b.backlinks)}
                    delta={links?.delta}
                    hint={hint([links?.since, t('MARKET.BACKLINKS.LINKS_HINT')])}
                    trend={historyTrend(history, 'backlinks')}
                />
                <KpiCard
                    label={t('MARKET.BACKLINKS.BROKEN')}
                    value={formatNumber(b.brokenBacklinks)}
                    delta={broken?.delta}
                    invert
                    hint={hint([broken?.since, t('MARKET.BACKLINKS.BROKEN_HINT')])}
                />
            </div>

            <div className="grid gap-4 lg:grid-cols-5">
                <SectionCard
                    className="lg:col-span-3"
                    title={<Term k="REFERRING_DOMAINS">{t('MARKET.BACKLINKS.TREND')}</Term>}
                    description={series.length > 1 ? t('BUSINESS.BACKLINKS.TREND_HINT') : t('BUSINESS.COMMON.ONE_POINT')}
                >
                    {series.length > 1 ? (
                        <TrendChart data={series} valueLabel={t('BUSINESS.TERMS.REFERRING_DOMAINS.LABEL')} height={200} />
                    ) : (
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                            <MiniStat label={t('BUSINESS.TERMS.REFERRING_DOMAINS.LABEL')} value={formatNumber(b.referringDomains)} />
                            <MiniStat label={t('BUSINESS.BACKLINKS.NETWORKS')} value={formatNumber(b.referringSubnets)} />
                            <MiniStat label={t('BUSINESS.BACKLINKS.FIRST_SEEN')} value={b.firstSeen ? formatDate(b.firstSeen.slice(0, 10)) : '—'} />
                        </div>
                    )}
                </SectionCard>

                <SectionCard className="lg:col-span-2" title={t('BUSINESS.BACKLINKS.QUALITY')} description={t('BUSINESS.BACKLINKS.QUALITY_HINT')}>
                    <div className="flex flex-col gap-5">
                        {follow && (
                            <div>
                                <p className="mb-2 text-xs text-muted-foreground">
                                    <Term k="NOFOLLOW">{t('BUSINESS.BACKLINKS.FOLLOW')}</Term>
                                </p>
                                <DistributionBar
                                    columns={2}
                                    segments={[
                                        { key: 'followed', label: t('BUSINESS.BACKLINKS.FOLLOWED'), value: follow.followed },
                                        { key: 'nofollow', label: t('BUSINESS.BACKLINKS.NOFOLLOWED'), value: follow.nofollow },
                                    ]}
                                />
                            </div>
                        )}
                        {spam && (
                            <div className="flex items-center justify-between gap-3 border-t pt-4">
                                <div className="min-w-0">
                                    <p className="text-xs text-muted-foreground">
                                        <Term k="SPAM" />
                                    </p>
                                    <StatusBadge tone={spam === 'high' ? 'danger' : spam === 'moderate' ? 'warning' : 'success'} className="mt-1">
                                        {t(`BUSINESS.BACKLINKS.SPAM.${spam.toUpperCase()}`)}
                                    </StatusBadge>
                                </div>
                                <MeterBar value={b.spamScore} invert label={`${formatNumber(b.spamScore)}/100`} width="w-20" />
                            </div>
                        )}
                        {diversity != null && (
                            <div className="flex items-center justify-between gap-3 border-t pt-4">
                                <div className="min-w-0">
                                    <p className="text-xs text-muted-foreground">{t('BUSINESS.BACKLINKS.DIVERSITY')}</p>
                                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                                        {t('BUSINESS.BACKLINKS.DIVERSITY_HINT', { networks: formatNumber(b.referringSubnets), sites: formatNumber(b.referringDomains) })}
                                    </p>
                                </div>
                                <MeterBar value={diversity * 100} label={`${formatNumber(Math.round(diversity * 100))}%`} width="w-20" />
                            </div>
                        )}
                        {!follow && !spam && diversity == null && <p className="text-sm text-muted-foreground">{t('BUSINESS.COMMON.NOT_IN_SNAPSHOT')}</p>}
                    </div>
                </SectionCard>
            </div>

            {composition.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                    {composition.map((c) => (
                        <SectionCard key={c.key} title={c.title} description={c.hint}>
                            {c.bar ? <DistributionBar columns={2} segments={c.items} /> : <BarList items={c.items.slice(0, 6)} />}
                        </SectionCard>
                    ))}
                </div>
            )}

            {(b.brokenBacklinks ?? 0) > 0 && (
                <SectionCard title={t('BUSINESS.BACKLINKS.FIX_TITLE')} description={t('BUSINESS.BACKLINKS.FIX_HINT', { links: formatNumber(b.brokenBacklinks), pages: formatNumber(b.brokenPages ?? 0) })}>
                    <ol className="flex flex-col gap-3">
                        {['ONE', 'TWO', 'THREE'].map((step, i) => (
                            <li key={step} className="flex items-start gap-3">
                                <span className="flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium tabular-nums">{i + 1}</span>
                                <span className="text-sm leading-relaxed">{t(`BUSINESS.BACKLINKS.FIX_STEP.${step}`)}</span>
                            </li>
                        ))}
                    </ol>
                </SectionCard>
            )}

            <SectionCard title={t('BUSINESS.BACKLINKS.IDEAS')} description={t('BUSINESS.BACKLINKS.IDEAS_HINT')}>
                <ul className="grid gap-3 sm:grid-cols-2">
                    {['SUPPLIERS', 'PROJECTS', 'LOCAL', 'TRADE'].map((idea) => (
                        <li key={idea} className="rounded-lg border p-3">
                            <p className="text-sm font-medium">{t(`BUSINESS.BACKLINKS.IDEA.${idea}`)}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t(`BUSINESS.BACKLINKS.IDEA.${idea}_HINT`)}</p>
                        </li>
                    ))}
                </ul>
            </SectionCard>
        </div>
    );
};
